import { useEffect, useRef } from "react";

const hourlyData = [
  { hour: "08", amount: 12500 },
  { hour: "09", amount: 28600 },
  { hour: "10", amount: 35200 },
  { hour: "11", amount: 42800 },
  { hour: "12", amount: 38500 },
  { hour: "13", amount: 25600 },
  { hour: "14", amount: 45200 },
  { hour: "15", amount: 38900 },
  { hour: "16", amount: 32100 },
  { hour: "17", amount: 28500 },
  { hour: "18", amount: 22300 },
];

const methodStats = [
  { method: "微信支付", amount: 285600, pct: 33, color: "#22c55e" },
  { method: "支付宝", amount: 198400, pct: 23, color: "#06b6d4" },
  { method: "银行代扣", amount: 245800, pct: 29, color: "#8b5cf6" },
  { method: "银行卡", amount: 85600, pct: 10, color: "#f59e0b" },
  { method: "现金", amount: 28400, pct: 3, color: "#E8531A" },
  { method: "网银转账", amount: 12400, pct: 2, color: "#64748b" },
];

export default function FeeStatsChart() {
  const barRef = useRef<HTMLCanvasElement>(null);
  const donutRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = barRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const w = canvas.width;
    const h = canvas.height;
    ctx.clearRect(0, 0, w, h);

    const maxVal = Math.max(...hourlyData.map(d => d.amount));
    const barW = (w - 40) / hourlyData.length;
    const chartH = h - 35;

    hourlyData.forEach((d, i) => {
      const x = 20 + i * barW;
      const bh = (d.amount / maxVal) * chartH;
      const y = chartH - bh;

      const grad = ctx.createLinearGradient(x, y, x, chartH);
      grad.addColorStop(0, "rgba(232,83,26,0.9)");
      grad.addColorStop(1, "rgba(232,83,26,0.2)");
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.roundRect(x + barW * 0.15, y, barW * 0.7, bh, 3);
      ctx.fill();

      ctx.fillStyle = "rgba(100,100,100,0.6)";
      ctx.font = "9px sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(`${d.hour}时`, x + barW / 2, h - 5);

      if (d.amount === maxVal) {
        ctx.fillStyle = "#E8531A";
        ctx.font = "bold 9px sans-serif";
        ctx.fillText(`¥${(d.amount / 1000).toFixed(0)}k`, x + barW / 2, y - 4);
      }
    });
  }, []);

  useEffect(() => {
    const canvas = donutRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const w = canvas.width;
    const h = canvas.height;
    ctx.clearRect(0, 0, w, h);

    const cx = w / 2;
    const cy = h / 2;
    const outerR = Math.min(w, h) / 2 - 10;
    const innerR = outerR * 0.55;

    let startAngle = -Math.PI / 2;
    methodStats.forEach(m => {
      const angle = (m.pct / 100) * Math.PI * 2;
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.arc(cx, cy, outerR, startAngle, startAngle + angle);
      ctx.closePath();
      ctx.fillStyle = m.color;
      ctx.fill();
      startAngle += angle;
    });

    // Inner circle (donut hole)
    ctx.beginPath();
    ctx.arc(cx, cy, innerR, 0, Math.PI * 2);
    ctx.fillStyle = "white";
    ctx.fill();

    // Center text
    ctx.fillStyle = "#1f2937";
    ctx.font = "bold 13px sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("¥856k", cx, cy + 2);
    ctx.fillStyle = "#9ca3af";
    ctx.font = "9px sans-serif";
    ctx.fillText("今日收款", cx, cy + 14);
  }, []);

  return (
    <div className="grid grid-cols-2 gap-4">
      {/* Hourly bar chart */}
      <div className="bg-white rounded-xl border border-gray-100 p-4">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-semibold text-gray-800">今日分时收费</span>
          <span className="text-xs text-gray-400">单位：元</span>
        </div>
        <canvas ref={barRef} width={400} height={160} className="w-full" />
        <div className="mt-2 grid grid-cols-3 gap-2">
          {[
            { label: "峰值时段", value: "14时", color: "text-[#E8531A]" },
            { label: "峰值金额", value: "¥45,200", color: "text-[#E8531A]" },
            { label: "今日笔数", value: "1,256笔", color: "text-gray-800" },
          ].map((s, i) => (
            <div key={i} className="bg-gray-50 rounded-lg p-2 text-center">
              <div className={`text-sm font-bold ${s.color}`}>{s.value}</div>
              <div className="text-[10px] text-gray-400">{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Payment method donut */}
      <div className="bg-white rounded-xl border border-gray-100 p-4">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-semibold text-gray-800">支付方式分布</span>
        </div>
        <div className="flex items-center gap-4">
          <canvas ref={donutRef} width={140} height={140} className="flex-shrink-0" />
          <div className="flex-1 space-y-2">
            {methodStats.map((m, i) => (
              <div key={i} className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: m.color }}></div>
                <span className="text-xs text-gray-600 flex-1">{m.method}</span>
                <span className="text-xs font-bold text-gray-800">{m.pct}%</span>
              </div>
            ))}
          </div>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2">
          {[
            { label: "最多使用", value: "微信支付 33%", color: "text-green-600" },
            { label: "代扣占比", value: "银行代扣 29%", color: "text-purple-600" },
          ].map((s, i) => (
            <div key={i} className="bg-gray-50 rounded-lg p-2">
              <div className="text-[10px] text-gray-400">{s.label}</div>
              <div className={`text-xs font-bold ${s.color}`}>{s.value}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
